import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { type FieldErrors, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { apiSchema } from '#api/model/types.ts';
import { useUpdateOrderMutation } from '#api-bridge/orderApi.ts';
import { reportErrorToSentry } from '#configuration/setup/sentry.ts';
import { mapWriteOrder } from '#utils/orders.util.ts';

import type { OrderFormData } from './OrderFormValidation.ts';

type EditOrderButtonToolbarProps = {
    orderId: string;
    submitOrder?: () => Promise<void>;
    handleClose: () => void;
    onValidationErrors: (errors: FieldErrors<OrderFormData>) => void;
};

export const EditOrderButtonToolbar = ({ orderId, handleClose, onValidationErrors }: EditOrderButtonToolbarProps) => {
    const intl = useIntl();
    const { handleSubmit } = useFormContext<OrderFormData>();

    const [updateOrder, { isLoading }] = useUpdateOrderMutation();

    const submitOrder = handleSubmit(async formValues => {
        try {
            const writeOrder = apiSchema.WriteOrder.parse(mapWriteOrder(formValues));
            await updateOrder({ id: orderId, order: writeOrder }).unwrap();

            Notification.success(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.orders.edit-dialog.success',

                    defaultMessage: 'Order successfully changed!',
                })
            );
            handleClose();
        } catch (e) {
            reportErrorToSentry(e);
        }
    }, onValidationErrors);

    const handleComplete = async () => {
        await submitOrder();
    };

    return (
        <ButtonToolbar alignment={'space-between'} className={'width-100pct'}>
            <Button
                bsStyle='default'
                autoTrackingKey='intl-msg:ordercom-cloud-frontend.orders.edit-dialog.cancel'
                onClick={handleClose}
            >
                <FormattedMessage id={'intl-msg:ordercom-cloud-frontend.orders.edit-dialog.cancel'} />
            </Button>
            <Button
                bsStyle='primary'
                autoTrackingKey='intl-msg:ordercom-cloud-frontend.orders.edit-dialog.save'
                onClick={() => handleComplete()}
                disabled={isLoading}
            >
                <FormattedMessage id={'intl-msg:ordercom-cloud-frontend.orders.edit-dialog.save'} />
            </Button>
        </ButtonToolbar>
    );
};
